import React from "react";
import { fetchGeotabDeviceAssociations, fetchGeotabDevices, fetchGeotabDeviceDrivers, saveGeotabDeviceOverride, saveGeotabDeviceReplacement } from "../../api/geotabApi.js";
import GeotabBadge, { DriverCard } from "./GeotabBadge.jsx";
import { isOtherDriver } from "./geotabFormat.js";

const digits = (text) => (text || "").replace(/\D/g, "");
const today = () => new Date().toLocaleDateString("en-CA");
const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short", year: "numeric" });
const CELL = "border-t border-slate-100 px-2 py-1.5 text-[12.5px]";
const HEAD = "px-2 py-1.5 text-left text-[10.5px] font-bold uppercase tracking-wide text-slate-500";
const INPUT = "h-6 rounded-[4px] border border-slate-300 bg-white py-0 text-[12.5px]";

// Tous les camions de la Flotte, avec ou sans Dispatch aujourd'hui. L'association suit le numéro du camion (19-05 → 1905) ;
// le droit « Gérer les boîtiers Geotab » permet d'en choisir un autre à partir d'une date d'effet : les journées d'avant ne changent pas.
export default function GeotabDevicesView({ canManage = false }) {
  const [state, setState] = React.useState({ fleet: null, unassigned: [], days: 30, live: [], devices: [], error: null });
  const [saving, setSaving] = React.useState(null);
  const [message, setMessage] = React.useState("");
  const [since, setSince] = React.useState({}); // date d'effet choisie, par camion ou par boîtier

  const load = React.useCallback(() => Promise.all([fetchGeotabDeviceAssociations(), fetchGeotabDeviceDrivers(), fetchGeotabDevices()])
    .then(([associations, live, devices]) => setState({ ...associations, live: live.trucks, devices: devices.devices, error: null }))
    .catch((error) => setState((s) => ({ ...s, error }))), []);
  React.useEffect(() => { load(); }, [load]);

  const save = async (key, action, done) => {
    setSaving(key);
    setMessage("");
    try {
      await action();
      await load();
      setMessage(done);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(null);
    }
  };
  const override = (row, deviceId, date) => save(row.truck_id, () => saveGeotabDeviceOverride(row.truck_id, deviceId, deviceId ? date : null),
    deviceId ? `Camion ${row.truck_name} associé manuellement à partir du ${fmtDay(date)}.` : `Camion ${row.truck_name} : association automatique rétablie.`);
  const replace = (device, truckId, date) => save(device.device_id, () => saveGeotabDeviceReplacement(device.device_id, truckId, truckId ? date : null),
    truckId ? `Boîtier ${device.device_name} : camion de remplacement à partir du ${fmtDay(date)}.` : `Boîtier ${device.device_name} : association retirée.`);

  if (state.error) return <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">{state.error.message}</p>;
  if (!state.fleet) return <p role="status" className="text-sm text-slate-500">Chargement des boîtiers…</p>;
  const devices = [...state.devices].sort((a, b) => a.name.localeCompare(b.name, "fr", { numeric: true }));
  // Chauffeurs du jour par boîtier : un camion qui tire une autre remorque que la sienne garde son chauffeur et sa session.
  const live = Object.fromEntries((state.live || []).map((r) => [r.device_id, r]));
  return (
    <div className="grid max-w-[1100px] gap-2">
      <p className="text-[12.5px] text-slate-600">
        Tous les camions de la Flotte, avec ou sans Dispatch aujourd'hui. L'association suit le numéro du camion (boîtier « 19-05 » → camion « 1905 »).
        {canManage ? " Un autre boîtier s'applique à partir de sa date d'effet ; « Automatique » revient au numéro." : " Le droit « Gérer les boîtiers Geotab » permet de la modifier."}
      </p>
      {message && <p role="status" className="text-[12.5px] font-semibold text-slate-700">{message}</p>}
      <table className="w-full overflow-hidden rounded-[4px] border border-slate-200 bg-white">
        <thead><tr>{["Camion", "Boîtier Geotab", "Association · depuis", "Chauffeur prévu", "Chauffeur connecté (Geotab)"].map((h) => <th key={h} className={HEAD}>{h}</th>)}</tr></thead>
        <tbody>
          {state.fleet.map((r) => {
            const auto = devices.find((d) => d.key && d.key === digits(r.truck_name));
            const date = since[r.truck_id] ?? r.since ?? today();
            const day = live[r.device_id];
            return (
              <tr key={r.truck_id}>
                <td className={`${CELL} font-bold text-slate-900`}>{r.truck_name}</td>
                <td className={CELL}>
                  {canManage ? (
                    <select aria-label={`Boîtier Geotab du camion ${r.truck_name}`} disabled={saving === r.truck_id} className={`${INPUT} pl-1.5 pr-6`}
                      value={r.source === "manual" ? r.device_id : ""} onChange={(e) => override(r, e.target.value, date)}>
                      <option value="">Automatique{auto ? ` (${auto.name})` : " (aucun)"}</option>
                      {devices.map((d) => <option key={d.id} value={d.id}>{d.name}{d.plate ? ` · ${d.plate}` : ""}</option>)}
                    </select>
                  ) : (r.device_name || "—")}
                </td>
                <td className={CELL}>
                  {!r.device_id ? <span className="inline-block w-24"><GeotabBadge tone="bad">Aucun boîtier</GeotabBadge></span>
                    : r.source !== "manual" ? "Automatique · toujours"
                      : canManage ? (
                        <span className="inline-flex items-center gap-1.5">Manuelle · depuis
                          <input type="date" aria-label={`Date d'effet du boîtier du camion ${r.truck_name}`} value={r.since || ""} disabled={saving === r.truck_id}
                            className={`${INPUT} px-1`} onChange={(e) => e.target.value && override(r, r.device_id, e.target.value)} />
                        </span>
                      ) : `Manuelle · ${r.since ? `depuis le ${fmtDay(r.since)}` : "toujours"}`}
                </td>
                <td className={CELL}>{day?.driver_plan || <span className="text-slate-400">—</span>}</td>
                <td className={CELL}>{day?.driver_geotab || <span className="text-slate-400">—</span>}
                  {day && isOtherDriver(day) ? <span className="ml-2 inline-block w-6 align-middle"><GeotabBadge tone="warn" title={`Chauffeur à confirmer · prévu : ${day.driver_plan}`}><DriverCard /></GeotabBadge></span> : null}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {state.unassigned.length > 0 && (
        <section className="rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
          <div className="px-1 pb-1 text-[12.5px] font-bold text-slate-800">{`Boîtiers conduits par vos chauffeurs, sans camion de la Flotte · ${state.days} derniers jours`}</div>
          <table className="w-full">
            <thead><tr>{["Boîtier", "Jours conduits", "Chauffeurs", "Association et date d'effet"].map((h) => <th key={h} className={HEAD}>{h}</th>)}</tr></thead>
            <tbody>
              {state.unassigned.map((d) => {
                const truckId = d.replacement?.truck_id || "";
                const date = since[d.device_id] ?? d.replacement?.since ?? today();
                const truck = state.fleet.find((t) => t.truck_id === truckId);
                return (
                  <tr key={d.device_id}>
                    <td className={`${CELL} font-bold text-slate-900`}>{d.device_name}</td>
                    <td className={CELL}>{d.days}</td>
                    <td className={CELL}>{d.drivers.join(", ")}</td>
                    <td className={CELL}>
                      {canManage ? (
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <select aria-label={`Association du boîtier ${d.device_name}`} disabled={saving === d.device_id} className={`${INPUT} pl-1.5 pr-6`}
                            value={truckId} onChange={(e) => replace(d, e.target.value, date)}>
                            <option value="">Aucune</option>
                            {state.fleet.map((t) => <option key={t.truck_id} value={t.truck_id}>{`Camion de remplacement de ${t.truck_name}`}</option>)}
                          </select>
                          à partir du
                          <input type="date" aria-label={`Date d'effet du boîtier ${d.device_name}`} value={date} disabled={saving === d.device_id} className={`${INPUT} px-1`}
                            onChange={(e) => {
                              const next = e.target.value;
                              setSince((prev) => ({ ...prev, [d.device_id]: next }));
                              if (truckId && next) replace(d, truckId, next);
                            }} />
                        </span>
                      ) : truck ? `Camion de remplacement de ${truck.truck_name} depuis le ${fmtDay(d.replacement.since || today())}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-1 pt-1 text-[11.5px] text-slate-500">
            Les arrêts faits avec ces boîtiers sont exclus des mesures du Calibrage. Associé comme camion de remplacement, un boîtier compte pour le camion remplacé à partir de sa date d'effet.
          </p>
        </section>
      )}
    </div>
  );
}
